import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Sparkles, RefreshCw, AlertTriangle, Copy, Check, Send, Loader2, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import MassEmailComposer from '../email/MassEmailComposer';

/**
 * AI Outreach Email Composer — Document 2 · Phase 5.2
 *
 * Live data via:
 *   POST /api/sales-intelligence/outreach-email/generate (gpt-5.2 dual-tone)
 *   GET  /api/sales-intelligence/outreach-email/:opp_id  (cached fetch)
 *   POST /api/sales-intelligence/outreach-email/override (audit log)
 *
 * DRAFT-ONLY. The component never sends email. "Use this draft" navigates
 * to /email-designer?subject=...&body=...&to=...&from_outreach=1&opportunity_id=...
 * The /email-designer page is responsible for the actual send.
 */

function ToneCard({ version, testidPrefix, locked, onUse, onUseMultiple, onCopy, copied }) {
  const [subject, setSubject] = useState(version.subject);
  const [body, setBody] = useState(version.body);

  useEffect(() => {
    setSubject(version.subject);
    setBody(version.body);
  }, [version.subject, version.body]);

  const labelTokens =
    version.tone === 'direct_confident'
      ? { ring: 'ring-stone-200', accent: 'text-stone-700', icon: '🎯' }
      : { ring: 'ring-amber-200', accent: 'text-amber-800', icon: '🤝' };

  const label = version.tone === 'direct_confident' ? 'Direct & Confident' : 'Warm & Consultative';

  return (
    <div className={`rounded-sm border bg-white p-4 ring-2 ${labelTokens.ring}`} data-testid={`${testidPrefix}-card`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-base" aria-hidden="true">{labelTokens.icon}</span>
          <h4 className={`text-sm font-semibold ${labelTokens.accent}`}>{label}</h4>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-semibold">
          {version.tone.replace(/_/g, ' ')}
        </Badge>
      </div>

      <label className="block text-[11px] uppercase tracking-wide font-semibold text-stone-500 mb-1">Subject</label>
      <Input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        className="text-sm mb-3"
        data-testid={`${testidPrefix}-subject`}
      />

      <label className="block text-[11px] uppercase tracking-wide font-semibold text-stone-500 mb-1">Body</label>
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={9}
        className="text-sm font-mono leading-relaxed mb-3"
        data-testid={`${testidPrefix}-body`}
      />

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          onClick={() => onUse({ subject, body, tone: version.tone })}
          disabled={locked}
          data-testid={`${testidPrefix}-use`}
          className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white disabled:bg-stone-300 disabled:text-stone-500"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          Use this draft
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onUseMultiple({ subject, body, tone: version.tone })}
          disabled={locked}
          data-testid={`${testidPrefix}-use-multiple`}
        >
          <Users className="h-3.5 w-3.5 mr-1.5" />
          Send to multiple
        </Button>
        <Button size="sm" variant="outline" onClick={() => onCopy({ subject, body })} data-testid={`${testidPrefix}-copy`}>
          {copied ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
          Copy
        </Button>
      </div>
    </div>
  );
}

export default function OutreachEmailComposer({ opportunityId, opportunity }) {
  const api = useApi();
  const navigate = useNavigate();

  const [data, setData] = useState(null); // {version_a, version_b, context, warnings, ...}
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [overrideAcknowledged, setOverrideAcknowledged] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [massComposerOpen, setMassComposerOpen] = useState(false);
  const [massInitial, setMassInitial] = useState({ subject: '', body: '' });

  // Initial load — try cached first
  useEffect(() => {
    if (!opportunityId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/sales-intelligence/outreach-email/${opportunityId}`);
        if (!cancelled && res && res.version_a && res.version_b) {
          setData(res);
        }
      } catch {
        // soft fail — user can click Generate
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  const handleGenerate = async (forceRefresh = false) => {
    if (!opportunityId) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await api.post('/sales-intelligence/outreach-email/generate', {
        opportunity_id: opportunityId,
        force_refresh: forceRefresh,
      });
      setData(res);
      // Reset override on regenerate
      if (forceRefresh) setOverrideAcknowledged(false);
      if (res?.daily_usage?.soft_warn) {
        toast.warning(`Heads up — ${res.daily_usage.used_today}/${res.daily_usage.cap} outreach generations used today.`);
      } else if (res?.cached) {
        toast.success('Loaded recent outreach drafts');
      } else {
        toast.success('AI outreach drafts ready · review before sending');
      }
    } catch (err) {
      const msg = err?.data?.detail?.message || err?.message || 'Generation failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleOverride = async () => {
    setOverrideAcknowledged(true); // optimistic UX
    try {
      await api.post('/sales-intelligence/outreach-email/override', {
        opportunity_id: opportunityId,
        reason: 'no_dm_coverage',
      });
    } catch (err) {
      // Override is local-state-driven; logging failure shouldn't break flow
      // but we surface it once.
      toast.error('Override logged locally but server-side audit failed.');
    }
  };

  const handleUse = (draft) => {
    const ctx = data?.context || {};
    const dm = ctx.primary_dm || {};
    const params = new URLSearchParams();
    if (draft.subject) params.set('subject', draft.subject);
    if (draft.body) params.set('body', draft.body);
    if (dm.email) params.set('to', dm.email);
    if (opportunityId) params.set('opportunity_id', opportunityId);
    params.set('from_outreach', '1');
    params.set('tone', draft.tone || '');
    navigate(`/email-designer?${params.toString()}`);
  };

  const handleUseMultiple = (draft) => {
    setMassInitial({
      subject: draft.subject || '',
      body: (draft.body || '').replace(/\n/g, '<br>'),
    });
    setMassComposerOpen(true);
  };

  const handleCopy = async ({ subject, body }, key) => {
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      // ignore
    }
  };

  const warnings = data?.warnings || [];
  const context = data?.context || {};
  const locked = warnings.includes('dm_coverage_zero') && !overrideAcknowledged;

  return (
    <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]" data-testid="outreach-email-composer">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#B89B5E]/10">
              <Mail className="h-5 w-5 text-[#B89B5E]" />
            </div>
            <div>
              <CardTitle className="text-lg">AI Outreach Email</CardTitle>
              <p className="text-xs text-muted-foreground">Two drafts · signal-aware · stage-aware · draft only</p>
            </div>
          </div>
          {data ? (
            <Button variant="outline" size="sm" onClick={() => handleGenerate(true)} disabled={generating} data-testid="outreach-regenerate">
              {generating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Regenerate
            </Button>
          ) : (
            <Button size="sm" onClick={() => handleGenerate(false)} disabled={generating || loading} data-testid="outreach-generate" className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white">
              {generating ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
              Generate Drafts
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {loading && !data && (
          <div className="flex items-center justify-center py-6 text-sm text-stone-500" data-testid="outreach-loading">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Checking for cached drafts…
          </div>
        )}

        {generating && !data && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-sm border border-stone-200 bg-stone-50" data-testid="outreach-generating">
            <Loader2 className="h-4 w-4 text-stone-700 animate-spin" />
            <span className="text-sm text-stone-700">Drafting two distinct tone variants…</span>
          </div>
        )}

        {!loading && !generating && !data && !error && (
          <div className="rounded-sm border border-dashed border-stone-200 px-4 py-6 text-center" data-testid="outreach-empty">
            <p className="text-sm text-stone-700">No drafts yet.</p>
            <p className="text-xs text-stone-500 mt-1">Click "Generate Drafts" to create signal-aware, stage-aware outreach drafts.</p>
          </div>
        )}

        {error && !data && (
          <div className="rounded-sm border border-red-200 bg-red-50 px-4 py-3" data-testid="outreach-error">
            <p className="text-sm font-semibold text-red-800">Generation unavailable</p>
            <p className="text-xs text-red-700 mt-0.5">{error}</p>
            <button type="button" onClick={() => handleGenerate(false)} className="mt-2 text-xs font-medium text-red-700 hover:text-red-900 underline-offset-2 hover:underline">
              Try again
            </button>
          </div>
        )}

        {data && (
          <>
            {/* DM coverage warning + override flow */}
            {warnings.includes('dm_coverage_zero') && (
              <div
                className={`rounded-sm border p-4 transition-opacity ${
                  overrideAcknowledged ? 'border-amber-200 bg-amber-50/40 opacity-70' : 'border-amber-300 bg-amber-50'
                }`}
                data-testid="dm-coverage-warning"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-900">No decision makers identified yet</p>
                    <p className="text-xs text-amber-800 mt-1">
                      Sending without a confirmed decision maker risks reaching the wrong inbox or being filtered as spam.
                      {overrideAcknowledged && (
                        <span className="block mt-1 text-amber-900 font-medium">
                          Override acknowledged — drafts unlocked. Review carefully before sending.
                        </span>
                      )}
                    </p>
                    {!overrideAcknowledged && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" data-testid="dm-add-cta" className="border-amber-300 text-amber-900 hover:bg-amber-100">
                          Add decision maker first
                        </Button>
                        <Button size="sm" onClick={handleOverride} data-testid="dm-override-btn" className="bg-amber-700 hover:bg-amber-800 text-white">
                          Send anyway (override)
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Similarity-low warning (non-blocking) */}
            {warnings.includes('similarity_low') && (
              <div className="rounded-sm border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-600" data-testid="similarity-warning">
                <span className="font-semibold text-stone-800">Note:</span> The two drafts are tonally close. Consider regenerating for stronger contrast.
              </div>
            )}

            {/* Context strip */}
            <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wide font-semibold text-stone-500">
              <span>Context:</span>
              {context.signal_type && (
                <Badge variant="outline" className="text-[10px]">signal · {String(context.signal_type).replace(/_/g, ' ')}</Badge>
              )}
              {(data.mode === 'manual_fallback' || warnings.includes('manual_fallback_mode')) && (
                <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-800 bg-amber-50" data-testid="outreach-mode-manual">
                  manual fallback · no signal
                </Badge>
              )}
              {context.stage && (
                <Badge variant="outline" className="text-[10px]">stage · {context.stage}</Badge>
              )}
              <Badge variant="outline" className="text-[10px]">dm · {context.coverage_count ?? 0}</Badge>
              {data.daily_usage && (
                <Badge variant="outline" className="text-[10px]">{data.daily_usage.used_today}/{data.daily_usage.cap} today</Badge>
              )}
            </div>

            {/* Two-version split pane */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dual-tone-grid">
              <ToneCard
                version={data.version_a}
                testidPrefix="version-a"
                locked={locked}
                onUse={handleUse}
                onUseMultiple={handleUseMultiple}
                onCopy={(p) => handleCopy(p, 'a')}
                copied={copiedKey === 'a'}
              />
              <ToneCard
                version={data.version_b}
                testidPrefix="version-b"
                locked={locked}
                onUse={handleUse}
                onUseMultiple={handleUseMultiple}
                onCopy={(p) => handleCopy(p, 'b')}
                copied={copiedKey === 'b'}
              />
            </div>

            <div className="flex items-center gap-2 pt-1 text-[11px] text-stone-500">
              <Sparkles className="h-3 w-3" />
              <span>Draft only — review and edit before sending.</span>
            </div>
          </>
        )}
      </CardContent>

      <MassEmailComposer
        open={massComposerOpen}
        onOpenChange={setMassComposerOpen}
        initialSubject={massInitial.subject}
        initialBody={massInitial.body}
        opportunityId={opportunityId}
      />
    </Card>
  );
}
